import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import momentTz from 'moment-timezone';

import { computeTournamentStatus } from '../../utils/tournamentStatus';

const statusCopy = {
  IN_PROGRESS: { label: 'In progress', dot: 'bg-[#00E7FF]', text: 'text-[#00E7FF]' },
  UPCOMING: { label: 'Upcoming', dot: 'bg-yellow-400', text: 'text-yellow-400' },
  COMPLETED: { label: 'Completed', dot: 'bg-green-500', text: 'text-green-500' },
};

const FLIP_INTERVAL_MS = 4000;
const FLIP_DURATION_MS = 700;

const formatStageValue = (stage) => {
  if (!stage) return '—';
  if (stage.currentDay && stage.totalDays) {
    return `Day ${stage.currentDay} / ${stage.totalDays}`;
  }
  return 'Upcoming';
};

const formatNextMatchValue = (nextMatches, timezone) => {
  if (!nextMatches || nextMatches.length === 0) return null;
  const first = nextMatches[0];
  if (!first?.matchTime) return null;
  const m = timezone ? momentTz(first.matchTime).tz(timezone) : momentTz(first.matchTime);
  const day = m.format('ddd');
  const time = m.format('ha'); // "3pm"
  const phase = first.phaseName || '';
  return [phase, day, time].filter(Boolean).join(' ');
};

const Card = ({ label, children }) => (
  <div className="bg-app-surface rounded-2xl p-4 md:p-5 flex flex-col gap-2 min-h-[88px] h-full">
    <p className="text-app-text-secondary text-[13px] m-0">{label}</p>
    <div className="text-app-text text-[18px] font-semibold leading-tight">
      {children}
    </div>
  </div>
);

// 3D flip wrapper. Both faces render a full <Card>; the hidden face is
// preloaded with the next category's data so when the flip transition runs
// the content is already in place — no flicker.
const FlipCard = ({ flipped, front, back }) => (
  <div className="relative h-full" style={{ perspective: 1000, minHeight: 88 }}>
    <div
      className="relative w-full h-full"
      style={{
        transformStyle: 'preserve-3d',
        transition: `transform ${FLIP_DURATION_MS}ms`,
        transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        minHeight: 88,
      }}
    >
      <div
        className="absolute inset-0"
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
      >
        {front}
      </div>
      <div
        className="absolute inset-0"
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
        }}
      >
        {back}
      </div>
    </div>
  </div>
);

const DashboardStatusRow = () => {
  const { selectedTournament, dashboard } = useSelector((state) => state.tournament);
  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const timezone = selectedTournament?.timezone;

  const isFresh = dashboard && dashboard.tournamentId === tournamentId;

  const status = useMemo(
    () => computeTournamentStatus(selectedTournament),
    [selectedTournament]
  );
  const statusInfo = status ? statusCopy[status] : null;

  const categories = isFresh && Array.isArray(dashboard.categories)
    ? dashboard.categories
    : [];
  const n = categories.length;

  // Two indices — one per face — so we can preload the hidden face's content
  // for the next tick and avoid mid-flip flicker. When the flip completes the
  // now-hidden face is bumped 2 categories ahead.
  const [frontIdx, setFrontIdx] = useState(0);
  const [backIdx, setBackIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  // Reset cycle whenever the tournament changes or the category list resizes.
  useEffect(() => {
    setFrontIdx(0);
    setBackIdx(n > 1 ? 1 : 0);
    setFlipped(false);
  }, [tournamentId, n]);

  useEffect(() => {
    if (n < 2) return undefined;
    const id = setInterval(() => {
      setFlipped((wasFlipped) => {
        const willBeFlipped = !wasFlipped;
        // After the flip animation completes, advance the now-hidden face to
        // be 2 categories ahead of what just became visible.
        setTimeout(() => {
          if (willBeFlipped) {
            setFrontIdx((i) => (i + 2) % n);
          } else {
            setBackIdx((i) => (i + 2) % n);
          }
        }, FLIP_DURATION_MS);
        return willBeFlipped;
      });
    }, FLIP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [n]);

  const frontCat = n > 0 ? categories[frontIdx] : null;
  const backCat = n > 0 ? categories[backIdx] : null;

  const renderStageCard = (cat) => {
    const stage = cat?.currentStage;
    const value = formatStageValue(stage);
    return (
      <Card label={stage?.stageName || 'Current stage'}>
        {value === '—'
          ? <span className="text-app-text-secondary">—</span>
          : <span>{value}</span>}
      </Card>
    );
  };

  const renderNextMatchCard = (cat) => {
    const value = formatNextMatchValue(cat?.nextMatches, timezone);
    return (
      <Card label="Next match">
        {value
          ? <span className="text-yellow-400">{value}</span>
          : <span className="text-app-text-secondary">—</span>}
      </Card>
    );
  };

  const renderTotalMatchesCard = (cat) => (
    <Card label="Total matches played">
      {cat?.totalMatches ? (
        <span>
          {cat.totalMatches.played} / {cat.totalMatches.total}
        </span>
      ) : (
        <span className="text-app-text-secondary">—</span>
      )}
    </Card>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <Card label="Tournament status">
        {statusInfo ? (
          <span className={`flex items-center gap-2 ${statusInfo.text}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${statusInfo.dot}`} />
            <span>{statusInfo.label}</span>
          </span>
        ) : (
          <span className="text-app-text-secondary">—</span>
        )}
      </Card>

      <FlipCard
        flipped={flipped}
        front={renderStageCard(frontCat)}
        back={renderStageCard(backCat)}
      />
      <FlipCard
        flipped={flipped}
        front={renderNextMatchCard(frontCat)}
        back={renderNextMatchCard(backCat)}
      />
      <FlipCard
        flipped={flipped}
        front={renderTotalMatchesCard(frontCat)}
        back={renderTotalMatchesCard(backCat)}
      />
    </div>
  );
};

export default DashboardStatusRow;
